import { useState, useCallback, useEffect } from 'react';
import { Pagination, Response, SelectItemProps } from '@ui/src';
import { PurposeCodeListItem } from '../../types';
import { useLazyGetTransactionsPurposeCodesListQuery } from '../services';

interface ListResponse {
  data: Response<PurposeCodeListItem>;
}

let timeout: NodeJS.Timeout;

const usePurposeCodesList = (type?: string) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [purposeCodesList, setPurposeCodesList] = useState<
    PurposeCodeListItem[]
  >([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [getList, { isFetching, isLoading }] =
    useLazyGetTransactionsPurposeCodesListQuery();

  const getPurposeCodeByCode = async (code: string) => {
    if (type) {
      const response = (await getList(
        `?all=true&search=${code}&type=${type}`,
      )) as ListResponse;

      if (response?.data?.data?.edges?.length) {
        setAutocompleteList(
          response?.data?.data?.edges?.map((item) => ({
            name: item.node.code || '',
            value: item.node.code,
            suffix: item.node.description,
          })),
        );
        setPurposeCodesList(
          response?.data?.data?.edges?.map((item) => item.node),
        );
      }
    }
  };

  const getAutocompleteListAction = useCallback(async () => {
    const results: SelectItemProps[] = [];
    const purposeCodes: PurposeCodeListItem[] = [];

    if (type) {
      const response = (await getList(`?take=20&type=${type}`)) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.code || '',
            value: item.node.code,
            suffix: item.node.description,
          });

          purposeCodes.push(item.node);
        });

        setPagination(response?.data?.data?.pageInfo);
      }
    }
    setAutocompleteList(results);
    setPurposeCodesList(purposeCodes);
  }, [type]);

  const onScroll = async () => {
    setNextPageIsLoading(true);

    const response = (await getList(
      `?take=20&cursor=${pagination?.endCursor}&type=${type}${searchValue ? `&search=${searchValue}` : ''}`,
    )) as ListResponse;

    if (response?.data?.data) {
      const nextAutocompleteList = ([] as SelectItemProps[]).concat(
        autocompleteList,
      );
      const nextPurposeCodesList = ([] as PurposeCodeListItem[]).concat(
        purposeCodesList,
      );

      response?.data?.data?.edges?.forEach((item) => {
        if (
          !nextAutocompleteList?.find(
            (listItem) => listItem.value === item.node.id,
          )
        ) {
          nextPurposeCodesList.push(item.node);
          nextAutocompleteList.push({
            name: item.node.code || '',
            value: item.node.code,
            suffix: item.node.description,
          });
        }
      });

      setAutocompleteList(nextAutocompleteList);
      setPurposeCodesList(nextPurposeCodesList);
      setNextPageIsLoading(false);
      setPagination(response?.data?.data?.pageInfo);
    }
  };

  const onSearch = async (search: string) => {
    setSearchValue(search);
    clearTimeout(timeout);

    timeout = setTimeout(async () => {
      const response = (await getList(
        `?take=20&type=${type}${search ? `&search=${search}` : ''}`,
      )) as ListResponse;

      if (response?.data?.data) {
        const results: SelectItemProps[] = [];
        const purposeCodes: PurposeCodeListItem[] = [];

        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.code || '',
            value: item.node.code,
            suffix: item.node.description,
          });

          purposeCodes.push(item.node);
        });

        setPagination(response?.data?.data?.pageInfo);
        setAutocompleteList(results);
        setPurposeCodesList(purposeCodes);
      }
    }, 700);
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    purposeCodesList,
    nextPageIsLoading,
    pagination,
    onScroll,
    getPurposeCodeByCode,
  };
};

export default usePurposeCodesList;
